// src/features/merchantConfig/components/FieldRow.jsx
//
// Single label-value row. The heart of the dense-payload UX: every scalar
// leaf in the configuration tree is rendered through this component.
// Branches at runtime on the value type per UI prompt §5 (view mode) and
// dispatches to the matching scalar editor in edit mode (Phase 2).
//
// Phase-2 behaviour:
//   * Read mode (mode === 'view'): same renderer as Phase 1.
//   * Edit mode (mode === 'edit'): swap the value cell for a scalar editor
//     whose initial value comes from the draft (not the original). Editor
//     commits via `useEditState().updateField(path, next)`.
//   * Field-level validation errors (URL/email/datetime/number) live in
//     `useEditState().fieldErrors` keyed by pathToKey(path).
//   * `data-readonly` data attribute on the row reflects the effective mode
//     so tests / screenshots can assert edit affordances.

import React, { useState, useCallback } from "react";
import {
  Copy,
  Check,
  ExternalLink,
  Mail,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useToast } from "../../../components/toast/ToastProvider";
import { humanizeLabel } from "../lib/humanizeLabel";
import { pathToKey } from "../lib/jsonPath";
import { inferType } from "../lib/typeCoercion";
import { useEditState } from "../state/EditStateContext";
import { isFieldEditable } from "../lib/editFieldPermissions";

import StringEditor from "./editors/StringEditor";
import TextAreaEditor from "./editors/TextAreaEditor";
import NumberEditor from "./editors/NumberEditor";
import BooleanEditor from "./editors/BooleanEditor";
import UrlEditor from "./editors/UrlEditor";
import EmailEditor from "./editors/EmailEditor";
import DateTimeEditor from "./editors/DateTimeEditor";
import ArrayOfScalarsEditor from "./editors/ArrayOfScalarsEditor";
import { DirtyDot, FieldError } from "./editors/editorChrome";

/* ---------- Type discriminators (runtime, type-safe - no `any`) ---------- */

const URL_RE = /^https?:\/\/\S+$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T/;

/** @type {(v: unknown) => v is string} */
const isString = (v) => typeof v === "string";
/** @type {(v: unknown) => v is number} */
const isNumber = (v) => typeof v === "number" && Number.isFinite(v);
/** @type {(v: unknown) => v is boolean} */
const isBoolean = (v) => typeof v === "boolean";

const LONG_STRING_THRESHOLD = 80;

/* ---------- Highlight helpers (for search match emphasis) ---------- */

function Highlighted({ text, highlights, className }) {
  if (!highlights || highlights.length === 0) {
    return <span className={className}>{text}</span>;
  }
  const sorted = [...highlights].sort((a, b) => a.start - b.start);
  const pieces = [];
  let cursor = 0;
  for (let i = 0; i < sorted.length; i += 1) {
    const { start, end } = sorted[i];
    if (start > cursor) pieces.push({ text: text.slice(cursor, start), hit: false });
    pieces.push({ text: text.slice(start, end), hit: true });
    cursor = end;
  }
  if (cursor < text.length) pieces.push({ text: text.slice(cursor), hit: false });
  return (
    <span className={className}>
      {pieces.map((p, i) =>
        p.hit ? (
          <mark key={i} className="rounded bg-yellow-200 px-0.5 text-gray-900">
            {p.text}
          </mark>
        ) : (
          <React.Fragment key={i}>{p.text}</React.Fragment>
        )
      )}
    </span>
  );
}

/* ---------- Value renderers (read mode) ---------- */

function CopyButton({ valueToCopy, ariaLabel }) {
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(
    async (e) => {
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(valueToCopy);
        setCopied(true);
        showToast("Copied to clipboard", "success", 1500);
        setTimeout(() => setCopied(false), 1200);
      } catch (err) {
        showToast("Couldn't copy to clipboard", "error", 2000);
      }
    },
    [valueToCopy, showToast]
  );

  return (
    <button
      type="button"
      onClick={onCopy}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 opacity-0 transition group-hover:opacity-100 hover:bg-blue-50 hover:text-blue-700 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      aria-label={ariaLabel || "Copy value"}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
    </button>
  );
}

function BooleanPill({ value }) {
  return value ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
      <span className="h-1.5 w-1.5 rounded-full bg-green-500" aria-hidden="true" />
      Enabled
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" aria-hidden="true" />
      Disabled
    </span>
  );
}

function NumberValue({ value }) {
  return (
    <span className="font-mono text-sm tabular-nums text-gray-900">
      {value.toLocaleString()}
    </span>
  );
}

function NullValue() {
  return (
    <span className="text-sm text-gray-400" aria-label="No value set">
      -
    </span>
  );
}

function UrlValue({ value, highlights }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 break-all text-sm text-blue-700 underline-offset-2 hover:underline"
      >
        <Highlighted text={value} highlights={highlights} />
        <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
      </a>
    </span>
  );
}

function EmailValue({ value, highlights }) {
  return (
    <a
      href={`mailto:${value}`}
      className="inline-flex items-center gap-1 break-all text-sm text-blue-700 underline-offset-2 hover:underline"
    >
      <Mail className="h-3 w-3 shrink-0" aria-hidden="true" />
      <Highlighted text={value} highlights={highlights} />
    </a>
  );
}

function IsoDateTimeValue({ value }) {
  let formatted = value;
  try {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      formatted = new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(d);
    }
  } catch {
    /* fallthrough - render the raw string */
  }
  return (
    <span title={value} className="text-sm text-gray-900">
      {formatted}
    </span>
  );
}

function LongStringValue({ value, highlights }) {
  const [expanded, setExpanded] = useState(false);
  const firstLine = value.split("\n")[0];
  const truncated =
    firstLine.length > LONG_STRING_THRESHOLD
      ? firstLine.slice(0, LONG_STRING_THRESHOLD) + "…"
      : firstLine;

  if (expanded) {
    return (
      <div className="space-y-1.5">
        <pre className="whitespace-pre-wrap break-words font-sans text-sm text-gray-900">
          <Highlighted text={value} highlights={highlights} />
        </pre>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
        >
          <ChevronUp className="h-3 w-3" /> Show less
        </button>
      </div>
    );
  }

  return (
    <span className="inline-flex items-baseline gap-2">
      <span className="text-sm text-gray-900">
        <Highlighted text={truncated} highlights={highlights} />
      </span>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
      >
        <ChevronDown className="h-3 w-3" /> Show more
      </button>
    </span>
  );
}

function ShortStringValue({ value, highlights }) {
  return (
    <span className="text-sm text-gray-900 break-words">
      <Highlighted text={value} highlights={highlights} />
    </span>
  );
}

/* ---------- Editor dispatcher (edit mode) ---------- */

/**
 * Pick the right editor for the original-value type and render it.
 * `displayValue` is the CURRENT draft value (which may have a different
 * type than the original - but per the OMS API the type must match the
 * stored type, so we lock the editor choice to the original's type).
 */
function ScalarEditorDispatch({
  originalValue,
  draftValue,
  ariaLabel,
  fieldError,
  inputId,
  onCommit,
  onInvalid,
}) {
  const type = inferType(originalValue);
  const hasError = Boolean(fieldError);

  switch (type) {
    case "boolean":
      return (
        <BooleanEditor
          inputId={inputId}
          value={draftValue}
          onCommit={onCommit}
          ariaLabel={ariaLabel}
        />
      );
    case "number":
      return (
        <NumberEditor
          inputId={inputId}
          value={draftValue}
          onCommit={onCommit}
          onInvalid={onInvalid}
          ariaLabel={ariaLabel}
          hasError={hasError}
        />
      );
    case "url":
      return (
        <UrlEditor
          inputId={inputId}
          value={draftValue}
          onCommit={onCommit}
          onInvalid={onInvalid}
          ariaLabel={ariaLabel}
          hasError={hasError}
        />
      );
    case "email":
      return (
        <EmailEditor
          inputId={inputId}
          value={draftValue}
          onCommit={onCommit}
          onInvalid={onInvalid}
          ariaLabel={ariaLabel}
          hasError={hasError}
        />
      );
    case "iso-datetime":
      return (
        <DateTimeEditor
          inputId={inputId}
          value={draftValue}
          onCommit={onCommit}
          onInvalid={onInvalid}
          ariaLabel={ariaLabel}
          hasError={hasError}
        />
      );
    case "long-string":
      return (
        <TextAreaEditor
          inputId={inputId}
          value={draftValue}
          onCommit={onCommit}
          ariaLabel={ariaLabel}
          hasError={hasError}
        />
      );
    case "array-of-scalars":
      return (
        <ArrayOfScalarsEditor
          inputId={inputId}
          value={draftValue}
          onCommit={onCommit}
          ariaLabel={ariaLabel}
          hasError={hasError}
        />
      );
    case "null":
      // Original was null/missing. Best we can do is offer a free-text string
      // editor - if the backend expects a different type the merchant will see
      // INVALID_PARAMETER_VALUE on save. The "Contact support to set up"
      // empty state is the official path for fields that were never set.
      return (
        <StringEditor
          inputId={inputId}
          value={draftValue ?? ""}
          onCommit={onCommit}
          ariaLabel={ariaLabel}
          hasError={hasError}
          placeholder="Empty"
        />
      );
    case "string":
    default:
      return (
        <StringEditor
          inputId={inputId}
          value={draftValue ?? ""}
          onCommit={onCommit}
          ariaLabel={ariaLabel}
          hasError={hasError}
        />
      );
  }
}

/* ---------- Top-level FieldRow ---------- */

/**
 * @param {{
 *   fieldKey: string,
 *   value: unknown,
 *   path: Array<string | number>,
 *   readOnly?: boolean,
 *   onEdit?: (path: Array<string | number>, newValue: unknown) => void,
 *   labelHighlights?: Array<{ start: number, end: number }>,
 *   valueHighlights?: Array<{ start: number, end: number }>,
 *   labelOverride?: string,
 *   configKey?: string,
 * }} props
 */
export default function FieldRow({
  fieldKey,
  value,
  path,
  readOnly = true,
  onEdit,
  labelHighlights,
  valueHighlights,
  labelOverride,
  configKey,
}) {
  const label = labelOverride ?? humanizeLabel(fieldKey);
  const editState = useEditStateOptional();
  const inEditMode = !readOnly && editState?.mode === "edit";

  const pathKey = pathToKey(path);
  const fieldError = inEditMode ? editState.fieldErrors.get(pathKey) : null;
  const isDirty = inEditMode ? editState.dirtyKeys.has(pathKey) : false;
  const editable =
    inEditMode &&
    isFieldEditable({ configKey: configKey || "", path });

  /* ---------- VIEW MODE: render the value as in Phase 1 ---------- */

  if (!inEditMode || !editable) {
    return (
      <ViewModeRow
        label={label}
        value={value}
        path={path}
        readOnly={readOnly || !editable}
        labelHighlights={labelHighlights}
        valueHighlights={valueHighlights}
        dirtyIndicator={isDirty}
      />
    );
  }

  /* ---------- EDIT MODE: dispatch to a scalar editor ---------- */

  const labelId = `field-${pathKey}-label`;
  const inputId = `field-${pathKey}-input`;

  const onCommit = (next) => {
    if (onEdit) onEdit(path, next);
    editState.updateField(path, next);
  };
  const onInvalid = (msg) => {
    editState.setFieldError(path, msg);
  };

  return (
    <div
      className="group grid grid-cols-1 items-start gap-1 rounded-md px-2 py-2 hover:bg-gray-50 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] sm:gap-4"
      data-readonly="false"
    >
      <dt
        id={labelId}
        className="flex items-center gap-1.5 truncate text-xs font-medium uppercase tracking-wide text-gray-500 sm:pt-2"
      >
        <label htmlFor={inputId}>
          <Highlighted text={label} highlights={labelHighlights} />
        </label>
        <DirtyDot visible={isDirty} />
      </dt>
      <dd className="min-w-0">
        <ScalarEditorDispatch
          originalValue={getOriginalAtPath(editState, path) ?? value}
          draftValue={value}
          ariaLabel={label}
          fieldError={fieldError}
          inputId={inputId}
          onCommit={onCommit}
          onInvalid={onInvalid}
        />
        <FieldError error={fieldError} />
      </dd>
    </div>
  );
}

/* ---------- Helpers ---------- */

/**
 * `useEditState()` throws if there is no provider above us - but FieldRow is
 * also reused outside the merchant-config page (e.g. inside ArrayTable's
 * expanded row), so we need a soft variant that returns null instead.
 */
function useEditStateOptional() {
  try {
    return useEditState();
  } catch {
    return null;
  }
}

function getOriginalAtPath(editState, path) {
  if (!editState || !editState.original) return undefined;
  let cursor = editState.original;
  for (let i = 0; i < path.length; i += 1) {
    if (cursor === null || cursor === undefined) return undefined;
    if (typeof cursor !== "object") return undefined;
    cursor = cursor[path[i]];
  }
  return cursor;
}

/**
 * Pure read-mode FieldRow extracted into a sub-component so the edit-mode
 * branch reads cleanly and doesn't have to reimplement the read renderers.
 */
function ViewModeRow({
  label,
  value,
  path,
  readOnly,
  labelHighlights,
  valueHighlights,
  dirtyIndicator,
}) {
  let valueNode;
  let copyTarget = null;
  let isCopyable = false;

  if (value === null || value === undefined) {
    valueNode = <NullValue />;
  } else if (isBoolean(value)) {
    valueNode = <BooleanPill value={value} />;
  } else if (isNumber(value)) {
    valueNode = <NumberValue value={value} />;
    copyTarget = String(value);
    isCopyable = true;
  } else if (isString(value)) {
    copyTarget = value;
    isCopyable = value.length > 0;

    if (URL_RE.test(value)) {
      valueNode = <UrlValue value={value} highlights={valueHighlights} />;
    } else if (EMAIL_RE.test(value)) {
      valueNode = <EmailValue value={value} highlights={valueHighlights} />;
    } else if (ISO_DATETIME_RE.test(value)) {
      valueNode = <IsoDateTimeValue value={value} />;
      copyTarget = value;
    } else if (value.length > LONG_STRING_THRESHOLD || value.includes("\n")) {
      valueNode = <LongStringValue value={value} highlights={valueHighlights} />;
    } else {
      valueNode = <ShortStringValue value={value} highlights={valueHighlights} />;
    }
  } else {
    valueNode = (
      <span className="font-mono text-xs text-gray-600">{String(value)}</span>
    );
  }

  const labelId = `field-${pathToKey(path)}-label`;
  const valueId = `field-${pathToKey(path)}-value`;

  return (
    <div
      className="group grid grid-cols-1 items-start gap-1 rounded-md px-2 py-2 hover:bg-gray-50 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] sm:gap-4"
      data-readonly={readOnly ? "true" : "false"}
    >
      <dt
        id={labelId}
        className="flex items-center gap-1.5 truncate text-xs font-medium uppercase tracking-wide text-gray-500 sm:pt-0.5"
      >
        <Highlighted text={label} highlights={labelHighlights} />
        <DirtyDot visible={dirtyIndicator} />
      </dt>
      <dd
        id={valueId}
        aria-labelledby={labelId}
        className="flex min-w-0 items-start gap-2"
      >
        <div
          className={`min-w-0 flex-1 ${
            isCopyable
              ? "cursor-pointer rounded-md transition hover:text-blue-700"
              : ""
          }`}
          onClick={async () => {
            if (!isCopyable || !copyTarget) return;
            try {
              await navigator.clipboard.writeText(copyTarget);
            } catch {
              /* swallow - the CopyButton has the user-visible toast */
            }
          }}
          role={isCopyable ? "button" : undefined}
          tabIndex={isCopyable ? 0 : undefined}
          onKeyDown={
            isCopyable
              ? (e) => {
                  if ((e.key === "Enter" || e.key === " ") && copyTarget) {
                    e.preventDefault();
                    navigator.clipboard.writeText(copyTarget).catch(() => {});
                  }
                }
              : undefined
          }
        >
          {valueNode}
        </div>
        {isCopyable && copyTarget !== null && (
          <CopyButton valueToCopy={copyTarget} ariaLabel={`Copy ${label}`} />
        )}
      </dd>
    </div>
  );
}
